import Backbone from 'backbone';
import $ from 'jquery';
import React from 'react';
import ReactDOM from 'react-dom';
import _ from 'underscore';

import {isBiologicsEnabled} from 'antibodies/util/biologicsCapability';
import BenchlingConfig from 'BenchlingConfig';
import {
  AA_SEQUENCE_MAP_Y_COMPONENTS,
  AA_SEQUENCE_MAP_Z_COMPONENTS,
  SliceComponentName,
} from 'constants/MapsConstants';
import AaSequence from 'molbio/aa-sequences/AaSequence';
import AaSequenceActions from 'molbio/aa-sequences/AaSequenceActions';
import AaSequenceInputBubble from 'molbio/aa-sequences/AaSequenceInputBubble';
import AaSequenceStore from 'molbio/aa-sequences/AaSequenceStore';
import * as ProteinProperties from 'molbio/aa-sequences/util/AaSequenceProperties';
import {listenToAntibodyVisibilitySettings} from 'molbio/antibody-engineering/visibilityHelpers';
import {INPUT_BUBBLE_OPERATION} from 'molbio/maps/InputBubble';
import PolymerLinearMapView from 'molbio/maps/linear/PolymerLinearMapView';
import PolymerSliceItemSource, {
  PolymerSliceItemSourceAttributes,
} from 'molbio/maps/linear/PolymerSliceItemSource';
import {AaSequenceLinearMapState} from 'molbio/maps/MapStates';
import AaSequenceMapOptions from 'molbio/maps/options/AaSequenceMapOptions';
import SimplifiedAaSequenceMapOptions from 'molbio/maps/options/SimplifiedAaSequenceMapOptions';
import {ShowInputBubbleOptions} from 'molbio/maps/SequenceMapUtil';
import Type from 'molbio/maps/Type';
import AaSequenceMapTabState from 'molbio/tabstates/AaSequenceMapTabState';
import notifications from 'notifications';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import addTestAttrsToDOMElement from 'test/lib/cypress/addTestAttrsToDOMElement';
import {selectRange} from 'toolbar/aa-sequence/AaSequenceUIGlobalState';
import * as bclip from 'util/BenchlingClipboard';

class AaSequenceSliceItemSource extends PolymerSliceItemSource<
  AaSequence,
  AaSequenceLinearMapState,
  AaSequenceMapTabState
> {
  override initialize(
    options: PolymerSliceItemSourceAttributes<AaSequence, AaSequenceLinearMapState, AaSequenceMapTabState>
  ): void {
    super.initialize(options);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.get('mapstate').protein(), 'change', this.update);
  }

  sliceViewY(): Array<SliceComponentName> {
    return AA_SEQUENCE_MAP_Y_COMPONENTS;
  }
  sliceViewZ(): Array<SliceComponentName> {
    return AA_SEQUENCE_MAP_Z_COMPONENTS;
  }
}

export function validateAndStripAminoAcids(text: string): string | null {
  // @ts-expect-error: it's safe to call on a string
  const [aminoAcidChars, invalidChars] = _.partition(text, (char: string) =>
    ProteinProperties.areAminoAcidsValid(char)
  );

  const aminoAcids = aminoAcidChars.join('');
  if (aminoAcids.length === 0) {
    notifications.info('No valid amino acids were pasted.');
    return null;
  }

  if (
    invalidChars.length > 0 &&
    // Only notify if skipped character wasn't whitespace
    invalidChars.some((char) => !/\s/.test(char))
  ) {
    notifications.info('Some amino acids pasted were not valid and were removed.');
  }
  return aminoAcids;
}

class AaSequenceMap extends PolymerLinearMapView<
  AaSequence,
  AaSequenceLinearMapState,
  AaSequenceMapTabState
> {
  $reactMount!: JQuery;
  currentOperation?: INPUT_BUBBLE_OPERATION;

  static initStatics(): void {
    this.prototype.id = 'aa-sequence-map';
    // inputSource is an analytics dimension: PolymerMapTabView reports it as
    // `source` on every `editor` event. Renaming it would split metric continuity
    // at the deploy, so it keeps the legacy value.
    this.prototype.inputSource = 'protein-map-input';
  }

  override initialize(...args: Array<unknown>): void {
    // @ts-expect-error
    super.initialize(...args);
    this.$reactMount = $('<div>', {class: 'js-reactMount'});
    this.$el.append(this.$reactMount);
    addTestAttrsToDOMElement(this.$el[0], {component: 'AaSequenceMapView', element: 'root'});
    this._handleRemoveInputBubble = this._handleRemoveInputBubble.bind(this);
  }

  createSliceItemSource(args: {
    mapstate: AaSequenceLinearMapState;
    tabstate: AaSequenceMapTabState;
  }): AaSequenceSliceItemSource {
    return new AaSequenceSliceItemSource(args);
  }

  addPolymersForSearch(): void {
    AaSequenceActions.addAaSequencesForSearch([this.model.protein().id]);
  }

  removePolymersForSearch(): void {
    AaSequenceActions.removeAaSequencesFromSearch([this.model.protein().id]);
  }

  storeRegionInBenchlingClipboard(e: JQueryEventObject): string {
    const aaSequence = this.mapstate.protein();
    const selection = aaSequence.selection().assertNotEmpty();
    const aminoAcids = aaSequence.residueSlice(selection.start(), selection.end());
    const clipboardDesc = bclip.clipboard.stdDesc(selection);
    bclip.clipboard.store(aminoAcids);
    return bclip.clipboard.getHash(aminoAcids, clipboardDesc);
  }

  getType(): Type {
    return this.mapstate.get('layout').AminoAcids.get('type');
  }

  handlePasteText(text: string): void {
    if (!this.model.protein().allowResidueEdits()) {
      return;
    }

    const aminoAcids = validateAndStripAminoAcids(text);
    if (aminoAcids == null) {
      return;
    }

    const aaSequence = this.model.protein();
    const start = aaSequence.selection().start();
    const end = aaSequence.selection().end();
    // eslint-disable-next-line benchlint/no-eqeqeq-null
    if (end !== null) {
      AaSequenceActions.replaceAminoAcids(aaSequence.id, {start, end, aminoAcids});
    } else {
      AaSequenceActions.insertAminoAcids(aaSequence.id, {position: start, aminoAcids});
    }
    selectRange(aaSequence.id, start, start + aminoAcids.length);
  }

  override undo(): void {
    const aaSequence = this.model.protein();
    if (!aaSequence.allowResidueEdits()) {
      return;
    }
    AaSequenceActions.undo(aaSequence.id);
  }

  handleCtrlShiftC(e: JQueryEventObject): void {
    // Proteins don't have a special copy menu. We prevent default behavior so the
    // element-select browser behavior in chrome doesn't trigger.
    e.preventDefault();
  }

  override _getBubbleOffsetClass(): string {
    return '.aminoAcids';
  }

  override repositionBubble(): void {
    if ($('.input-bubble').length > 0) {
      this.showInputBubble({operation: this.currentOperation});
    }
  }

  // TODO: Consolidate with SequenceMapUtil.showInputBubble
  showInputBubble(options?: ShowInputBubbleOptions): void {
    const pos = this._calculateBubblePos();
    if (pos == null) {
      this._handleRemoveInputBubble();
      return;
    }

    const {top, left, offset, isPositionedAbove} = pos;
    const aaSequence = this.mapstate.protein();
    const selection = aaSequence.selection();
    this.currentOperation =
      options?.operation ??
      (selection.isNotEmpty() ? INPUT_BUBBLE_OPERATION.REPLACE : INPUT_BUBBLE_OPERATION.INSERT);

    renderToDOM(
      <AaSequenceInputBubble
        proteinId={aaSequence.id}
        selection={selection}
        top={top}
        left={left}
        offset={offset}
        isPositionedAbove={isPositionedAbove}
        // eslint-disable-next-line @typescript-eslint/unbound-method
        onRequestClose={this._handleRemoveInputBubble}
        pasteText={options?.pasteText ?? ''}
        operation={this.currentOperation}
      />,
      this.$reactMount[0]
    );
  }

  _handleRemoveInputBubble(): void {
    ReactDOM.unmountComponentAtNode(this.$reactMount[0]);
    // this.inputBubbleView is always null on the protein map since we use the React input bubble instead
    this.inputBubbleView = null;
  }

  isValidCharacter(char: string): boolean {
    return ProteinProperties.areAminoAcidsValid(char);
  }
}
AaSequenceMap.initStatics();

class AaSequenceMapOptionsView extends Backbone.View<AaSequenceMapTabState> {
  mapstate!: AaSequenceLinearMapState;

  override initialize(): void {
    this.mapstate = this.model.get('mapstate');
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.mapstate, 'change:zoom', this.render);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.mapstate.get('polymer'), 'change:banner', this.render);
    AaSequenceStore.addChangeListener(() => this.render());
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.mapstate.get('visibilityOptions'), 'change', this.render);
    if (BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS && !isBiologicsEnabled()) {
      listenToAntibodyVisibilitySettings(
        this.mapstate.get('visibilityOptions'),
        this,
        this.render.bind(this)
      );
    }
  }

  override render(): this {
    const aaSequence = this.mapstate.get('polymer');
    renderToDOM(
      <AaSequenceMapOptions
        aminoAcids={aaSequence.aminoAcids()}
        onSearch={() => {
          AaSequenceActions.openSearchView();
        }}
        zoomSliderProps={{
          min: this.mapstate.get('minZoom'),
          max: this.mapstate.get('maxZoom'),
          value: this.mapstate.get('zoom'),
          step: this.mapstate.get('stepZoom'),
          disabled: false,
          onChange: (zoom) => this.mapstate.setZoom(zoom),
          zoomBigger: () => this.mapstate.changeZoomByStep(10),
          zoomSmaller: () => this.mapstate.changeZoomByStep(-10),
        }}
        visibilityOptions={this.mapstate.get('visibilityOptions')}
        featureCounts={{
          annotations: aaSequence.annotations().length,
          parts: aaSequence.parts().length,
        }}
        proteinId={aaSequence.id}
        isLatest={aaSequence.isLatest()}
        allowResidueEdits={aaSequence.allowResidueEdits()}
        isArchived={aaSequence.isArchived()}
        isContainedInComplexPolymerData={aaSequence.isContainedInComplexPolymerData()}
        allowFeatureEdits={aaSequence.allowFeatureEdits() ?? false}
        permissions={aaSequence.permissions()}
      />,
      this.el
    );
    return this;
  }
}

class SimplifiedAaSequenceMapOptionsView extends Backbone.View<AaSequenceMapTabState> {
  mapstate!: AaSequenceLinearMapState;

  override initialize(): void {
    this.mapstate = this.model.get('mapstate');
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.mapstate, 'change:zoom', this.render);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.mapstate.get('polymer'), 'change:banner', this.render);
    AaSequenceStore.addChangeListener(() => this.render());
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.mapstate.get('visibilityOptions'), 'change', this.render);
  }

  override render(): this {
    renderToDOM(
      <SimplifiedAaSequenceMapOptions
        zoomSliderProps={{
          min: this.mapstate.get('minZoom'),
          max: this.mapstate.get('maxZoom'),
          value: this.mapstate.get('zoom'),
          step: this.mapstate.get('stepZoom'),
          disabled: false,
          onChange: (zoom) => this.mapstate.setZoom(zoom),
          zoomBigger: () => this.mapstate.changeZoomByStep(10),
          zoomSmaller: () => this.mapstate.changeZoomByStep(-10),
        }}
        visibilityOptions={this.mapstate.get('visibilityOptions')}
        featureCounts={{annotations: this.mapstate.get('polymer').annotations().length}}
        aminoAcids={this.mapstate.get('polymer').aminoAcids()}
      />,
      this.el
    );
    return this;
  }
}

export {
  AaSequenceMap as TabView,
  AaSequenceMapOptionsView as OptionsView,
  SimplifiedAaSequenceMapOptionsView as SimplifiedOptionsView,
};
